import { useEffect, useState, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Image } from "primereact/image";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { classNames } from "primereact/utils";
import { passengerAdmin, getAdminConfigWarning, asAdminList } from "../components/services/adminBackendFetch";
import { useAppContext } from "../providers/ProviderAppContainer";
import { AdminPageHeader } from "../components/AdminPageHeader";
import { AdminTableShell } from "../components/AdminTableShell";
import { formatAdminDateTime } from "../utils/formatAdminDateTime";

export default function PassengersPage() {
    const { showToast } = useAppContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [editForm, setEditForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        active: true,
    });

    const reload = useCallback(() => {
        const warn = getAdminConfigWarning();
        if (warn) {
            showToast({ severity: "warn", summary: "Configuration", detail: warn });
            setLoading(false);
            return Promise.resolve();
        }
        setLoading(true);
        return passengerAdmin("/admin/passengers")
            .then((data) => {
                const list = asAdminList(data);
                if (!Array.isArray(data)) {
                    showToast({
                        severity: "warn",
                        summary: "Unexpected response",
                        detail: "The passenger list was not in the expected format.",
                    });
                }
                setRows(list);
            })
            .catch((e) => {
                showToast({
                    severity: "error",
                    summary: "Could not load passengers",
                    detail: String(e.message || e),
                });
                setRows([]);
            })
            .finally(() => setLoading(false));
    }, [showToast]);

    useEffect(() => {
        reload();
    }, [reload]);

    async function openEdit(row) {
        setEditOpen(true);
        setEditRecord(null);
        setEditLoading(true);
        try {
            const p = await passengerAdmin(`/admin/passengers/${row.id}`);
            setEditRecord(p);
            setEditForm({
                full_name: p.full_name ?? "",
                email: p.email ?? "",
                phone: p.phone != null ? String(p.phone) : "",
                active: !!p.active,
            });
        } catch (e) {
            showToast({
                severity: "error",
                summary: "Load passenger",
                detail: String(e.message || e),
            });
            setEditOpen(false);
        } finally {
            setEditLoading(false);
        }
    }

    async function saveEdit() {
        if (!editRecord) return;
        setEditSaving(true);
        try {
            const updated = await passengerAdmin(`/admin/passengers/${editRecord.id}`, {
                method: "PATCH",
                body: JSON.stringify({
                    full_name: editForm.full_name,
                    email: editForm.email,
                    phone: editForm.phone,
                    active: editForm.active,
                }),
            });
            showToast({ severity: "success", summary: "Saved", detail: updated?.full_name || "Passenger updated" });
            setEditOpen(false);
            await reload();
        } catch (e) {
            showToast({
                severity: "error",
                summary: "Save failed",
                detail: String(e.message || e),
            });
        } finally {
            setEditSaving(false);
        }
    }

    function requestDelete(row) {
        const name = (row.full_name || "").trim() || `ID ${row.id}`;
        confirmDialog({
            message: `Permanently delete passenger ${name}? This cannot be undone. Passengers with ride history cannot be deleted.`,
            header: "Delete passenger",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            accept: async () => {
                try {
                    await passengerAdmin(`/admin/passengers/${row.id}`, { method: "DELETE" });
                    showToast({ severity: "success", summary: "Deleted", detail: name });
                    await reload();
                } catch (e) {
                    showToast({
                        severity: "error",
                        summary: "Delete failed",
                        detail: String(e.message || e),
                    });
                }
            },
        });
    }

    const actionsBody = (row) => (
        <div className="uc-admin-table-actions">
            <Button label="Edit" icon="pi pi-pencil" size="small" outlined onClick={() => openEdit(row)} />
            <Button
                label="Delete"
                icon="pi pi-trash"
                size="small"
                severity="danger"
                outlined
                onClick={() => requestDelete(row)}
            />
        </div>
    );

    const avatarBody = (row) =>
        row.image ? (
            <Image src={row.image} alt="" width="32" preview className="border-round-md" />
        ) : (
            <span className="text-500">—</span>
        );

    const editDialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setEditOpen(false)} disabled={editSaving} />
            <Button label="Save" icon="pi pi-check" loading={editSaving} onClick={saveEdit} disabled={editLoading || !editRecord} />
        </div>
    );

    return (
        <div>
            <ConfirmDialog />
            <AdminPageHeader
                title="Passengers"
                subtitle="Customer accounts from the rider app — names, contact details, and registration status."
                meta={!loading ? `${rows.length} passenger${rows.length === 1 ? "" : "s"}` : undefined}
                actions={
                    <Button
                        label="Refresh"
                        icon="pi pi-refresh"
                        className="p-button-outlined"
                        onClick={reload}
                        disabled={loading}
                    />
                }
            />
            {loading ? (
                <div className="flex justify-content-center p-6">
                    <ProgressSpinner />
                </div>
            ) : (
                <AdminTableShell
                    header={
                        <>
                            <i className="pi pi-table text-primary" aria-hidden />
                            <span className="uc-admin-table-toolbar-title">Passenger directory</span>
                        </>
                    }
                >
                    <DataTable
                        value={rows}
                        paginator
                        paginatorDropdownAppendTo={typeof document !== "undefined" ? document.body : undefined}
                        rows={15}
                        rowsPerPageOptions={[15, 25, 50]}
                        stripedRows
                        emptyMessage="No passengers found."
                        size="small"
                        showGridlines
                        responsiveLayout="stack"
                        breakpoint="768px"
                        className="uc-admin-datatable-fluid"
                    >
                        <Column
                            field="id"
                            header="ID"
                            sortable
                            body={(r) => <span className="uc-admin-cell-nowrap">{r.id}</span>}
                        />
                        <Column header="Avatar" body={avatarBody} />
                        <Column field="full_name" header="Name" sortable />
                        <Column
                            field="phone"
                            header="Phone"
                            sortable
                            body={(r) => <span className="uc-admin-cell-nowrap">{r.phone ?? "—"}</span>}
                        />
                        <Column field="email" header="Email" />
                        <Column
                            field="active"
                            header="Active"
                            body={(r) => (
                                <Tag value={r.active ? "Yes" : "No"} severity={r.active ? "success" : "secondary"} />
                            )}
                        />
                        <Column
                            field="created_on"
                            header="Created"
                            sortable
                            body={(r) => (
                                <span className="uc-admin-cell-nowrap">{formatAdminDateTime(r.created_on)}</span>
                            )}
                        />
                        <Column header="Actions" body={actionsBody} />
                    </DataTable>
                </AdminTableShell>
            )}

            <Dialog
                header={editRecord ? `Edit passenger #${editRecord.id}` : "Edit passenger"}
                visible={editOpen}
                onHide={() => setEditOpen(false)}
                footer={editDialogFooter}
                style={{ width: "min(28rem, 96vw)" }}
                className="uc-admin-dialog"
                draggable={false}
                dismissableMask
                pt={{
                    content: { className: classNames("uc-admin-dialog-content", "border-round-bottom-xl") },
                }}
            >
                {editLoading ? (
                    <div className="flex justify-content-center p-5">
                        <ProgressSpinner />
                    </div>
                ) : editRecord ? (
                    <div className="flex flex-column gap-3 p-fluid">
                        <div className="flex flex-column gap-2">
                            <label htmlFor="p-name" className="font-medium text-sm">
                                Full name
                            </label>
                            <InputText
                                id="p-name"
                                value={editForm.full_name}
                                onChange={(e) => setEditForm((f) => ({ ...f, full_name: e.target.value }))}
                            />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="p-email" className="font-medium text-sm">
                                Email
                            </label>
                            <InputText
                                id="p-email"
                                type="email"
                                value={editForm.email}
                                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                            />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="p-phone" className="font-medium text-sm">
                                Phone
                            </label>
                            <InputText
                                id="p-phone"
                                value={editForm.phone}
                                onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                            />
                        </div>
                        <div className="flex align-items-center gap-2">
                            <InputSwitch
                                inputId="p-active"
                                checked={editForm.active}
                                onChange={(e) => setEditForm((f) => ({ ...f, active: !!e.value }))}
                            />
                            <label htmlFor="p-active" className="font-medium text-sm">
                                Account active
                            </label>
                        </div>
                    </div>
                ) : null}
            </Dialog>
        </div>
    );
}
